import React, { useState } from "react";
import { RotateCcw, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { orderBalance, isAdjusted } from "../../lib/orderMath";
import { shortDate } from "../../lib/shipping";

const errMsg = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function Row({ label, value, strong, testid }) {
  return (
    <div className={`flex justify-between ${strong ? "font-semibold text-[var(--terracotta)]" : "text-[var(--ink-forest)]"}`} data-testid={testid}>
      <span className={strong ? "" : "text-[var(--muted-fg)]"}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function RefundDialog({ order, balance, onClose, onUpdated }) {
  const [step, setStep] = useState("form");
  const [amount, setAmount] = useState(balance.refundDue > 0 ? balance.refundDue.toFixed(2) : "");
  const [reason, setReason] = useState("");
  const [emailCustomer, setEmailCustomer] = useState(false);
  const [sending, setSending] = useState(false);
  const [idemKey] = useState(newKey);
  const first = (order.customer_name || "the customer").split(" ")[0];
  const value = Math.round((parseFloat(amount) || 0) * 100) / 100;

  const review = () => {
    if (value <= 0) return toast.error("Enter an amount to refund");
    if (value > balance.refundable) return toast.error(`You can refund up to ${formatPrice(balance.refundable)}`);
    setStep("confirm");
  };

  const confirm = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/refunds`, { amount: value, reason, email_customer: emailCustomer, idempotency_key: idemKey });
      onUpdated(r.data.order);
      const emailed = emailCustomer ? (r.data.refund.customer_emailed ? ` and ${first} has been emailed` : ", but the email couldn't be sent") : "";
      toast.success(`Refunded ${formatPrice(value)}${emailed}`);
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "The refund didn't go through"));
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[var(--ink-forest)]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="refund-dialog-overlay">
      <div className="bg-[var(--bg-cream)] w-full max-w-md rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="refund-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <div>
            <p className="eyebrow text-[var(--terracotta)]">Refund</p>
            <p className="font-serif text-lg text-[var(--ink-forest)]">{order.customer_name} · {order.order_number}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--muted)]" aria-label="Close" data-testid="refund-close-btn"><X className="w-5 h-5" /></button>
        </div>
        {step === "form" ? (
          <div className="p-6 space-y-4">
            <label className="block">
              <span className="text-xs text-[var(--muted-fg)]">Amount (up to {formatPrice(balance.refundable)})</span>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[var(--muted-fg)]">$</span>
                <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="flex-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg px-3 py-2 text-base" data-testid="refund-amount-input" />
              </div>
              {balance.refundDue > 0 && <span className="text-[11px] text-[var(--muted-fg)]">Pre-filled with the refund due ({formatPrice(balance.refundDue)}).</span>}
            </label>
            <label className="block">
              <span className="text-xs text-[var(--muted-fg)]">Short reason</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. 5 Woodstock toys were sold out" className="w-full mt-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm" data-testid="refund-reason-input" />
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--ink-forest)] cursor-pointer">
              <input type="checkbox" checked={emailCustomer} onChange={(e) => setEmailCustomer(e.target.checked)} className="w-4 h-4 accent-[var(--terracotta)]" data-testid="refund-email-checkbox" />
              Email {first} a short refund note
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={review} className="bg-[var(--ink-forest)] text-[var(--bg-cream)] rounded-full px-5 py-2.5 text-sm font-medium" data-testid="refund-review-btn">Review refund</button>
              <button onClick={onClose} className="text-xs text-[var(--muted-fg)] underline">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4" data-testid="refund-confirm-step">
            <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 text-sm space-y-1.5">
              <Row label="Refund to card" value={formatPrice(value)} strong testid="refund-confirm-amount" />
              <Row label="Reason" value={reason || "—"} />
              <Row label={`Email ${first}`} value={emailCustomer ? "Yes" : "No"} />
            </div>
            <p className="text-xs text-[var(--muted-fg)] leading-relaxed" data-testid="refund-confirm-hint">
              {order.practice_refunds
                ? "This is a practice refund: it's recorded on the order, but no money moves. Real card refunds work once the shop is live."
                : `The money goes back to the card ${first} paid with, usually within 5–10 business days. Stripe doesn't return its original processing fee.`}
            </p>
            <div className="flex gap-2">
              <button onClick={confirm} disabled={sending} className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2" data-testid="refund-confirm-btn">
                <RotateCcw className="w-4 h-4" /> {sending ? "Refunding…" : `Refund ${formatPrice(value)}`}
              </button>
              <button onClick={() => setStep("form")} disabled={sending} className="text-xs text-[var(--muted-fg)] underline">Back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RefundSection({ order, onUpdated }) {
  const [open, setOpen] = useState(false);
  const balance = orderBalance(order);
  const refunds = order.refunds || [];

  return (
    <div className="space-y-3" data-testid="refund-section">
      {isAdjusted(order) && (
        <div className="bg-[var(--bg-cream)] border border-[var(--border)] rounded-lg p-3 text-sm space-y-1" data-testid="order-balance">
          <Row label="Paid" value={formatPrice(balance.paid)} testid="balance-paid" />
          <Row label="Value of what's being sent" value={formatPrice(balance.sending)} testid="balance-sending" />
          {balance.refunded > 0 && <Row label="Refunded so far" value={`−${formatPrice(balance.refunded)}`} testid="balance-refunded" />}
          <Row label="Refund due" value={formatPrice(balance.refundDue)} strong testid="balance-refund-due" />
        </div>
      )}

      {refunds.map((r) => (
        <p key={r.id} className="text-xs text-[var(--ink-forest)] inline-flex items-start gap-1.5 w-full" data-testid={`refund-row-${r.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5 text-[var(--terracotta)] shrink-0 mt-px" />
          <span>
            Refunded {formatPrice(r.amount)} on {shortDate(r.created_at)}
            {r.reason ? ` · ${r.reason}` : ""}
            {r.customer_emailed ? " · customer emailed" : ""}
            {r.practice && <span className="ml-1 text-[var(--muted-fg)]" data-testid={`refund-practice-${r.id}`}>(practice refund, no money moved)</span>}
          </span>
        </p>
      ))}

      {balance.refundable > 0 && (
        <button onClick={() => setOpen(true)} className="bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-4 py-2 text-sm font-medium hover:border-[var(--terracotta)]/40 inline-flex items-center gap-2" data-testid="order-refund-btn">
          <RotateCcw className="w-4 h-4" /> {balance.refundDue > 0 ? `Refund ${formatPrice(balance.refundDue)}` : "Refund"}
        </button>
      )}

      {open && <RefundDialog order={order} balance={balance} onClose={() => setOpen(false)} onUpdated={onUpdated} />}
    </div>
  );
}
